/**
 * A block root is a div where a block is rendererd.
 *
 * It has its id set to the block's hrid.
 * Further, it has data-block set to the block name and version in a specific format.
 *
 * These attributes are important for custom code to work, because that's how
 * advanced users can identify the blocks and do custom styling or actions on them.
 * They're also used by the blocks themselves for identifying linked blocks and various
 * other things.
 *
 * Block roots are managed outside of react because custom code might move
 * the block to a different location in the DOM, which might be outside
 * of the div where ReactDOM.createRoot is called at the root of the app.

 * Non react blocks are handled differently. They can contain multiple nodes,
 * so there's no block root for them. Instead, they're surrounded with sentinel
 * elements that mark the start and end of the block. This way, we can identify
 * them and move them around if needed.
 */
import { BlockDefinition, PAGE_RENDERER_CONSTANTS } from "@softr-io/blocks-sdk";
import { PreCompiledBlockDefinition, isPreCompiledBlock } from "./PreCompiledBlockDefinition";
import { keyBy } from "lodash-es";
import { DOMCursor } from "./DOMCursor";
import { nodesBetween } from "./dom-utils/nodesBetween";
import { log } from "./logger";

export { getBlockRootsByBlockId };

function findBlockRoot(blockId: string): HTMLElement | null {
    return document.querySelector(`[data-spr-block-id="${blockId}"]`);
}

export const DYNAMIC_HEIGHT_BLOCKS = [
    "conditionalForm",
    "forgot-password",
    "reset-password",
    "signIn",
    "signUp",
    "account-settings",
    "onboarding",
    "standaloneForm",
];

function initializeDynamicBlockRoot(definition: BlockDefinition) {
    const blockRoot = document.createElement("div");
    if (definition.name.toLowerCase().startsWith("header") && !definition.elements.verticalEnabled) {
        blockRoot.classList.add(PAGE_RENDERER_CONSTANTS.HORIZONTAL_HEADER_CLASS_NAME);
    }

    // This is required because the "Scroll to" action button uses
    // it as an href to scroll to the block.
    blockRoot.id = definition.hrid;

    // The data-block and class are important for styling
    blockRoot.classList.add(`block-${definition.id}`);

    // This is used by some platform scripts.
    // e.g. https://github.com/softr-io/softr-platform/blob/9d189d3f8425f123f83fcd804b14c06b287f034d/src/main/resources/templates/html/page.html#L890
    blockRoot.setAttribute("category", definition.category);

    // This is used by the block component as a prefix selector for styles
    // so that block stylesheets are scoped to the correct block.
    blockRoot.setAttribute("data-block", blockRootDataRootAttribute(definition));

    // The following 2 attributes are used by this file internally to
    // identify block roots. They have no other impact outside this file.
    blockRoot.setAttribute("data-spr-block-id", definition.id);
    blockRoot.setAttribute("data-spr-role", "block-root");
    blockRoot.setAttribute(BLOCK_HRID_ATTRIBUTE, definition.hrid);

    if (DYNAMIC_HEIGHT_BLOCKS.includes(definition.name)) {
        blockRoot.classList.add("dynamic-height");
    }

    return blockRoot;
}

/**
 * Get the value of the data-block attribute for the block root component.
 *
 * Within the page renderer, this is used by the Block component to
 * prefix all CSS rules with a block-specific selector.
 * E.g. if a stylesheet for the list17 block contains a rule such as
 * ```css
 * .foo {
 *   color: red;
 * }
 * ```
 * The block component converts it into
 * ```css
 * [data-block="list17-v5-0-0"] .foo {
 *   color: red;
 * }
 * ```
 *
 * Further, this is also passed to the Block component itself as
 * `parentSelector` prop. I'm not sure how blocks actually use this value, if
 * they use it at all.
 */
export function blockRootDataRootAttribute(definition: Pick<BlockDefinition, "name" | "version">) {
    return `${definition.name}-v${definition.version.replace(/\./g, "-")}`;
}

function allBlockRoots(portalDiv: HTMLElement): HTMLElement[] {
    return Array.from(portalDiv.querySelectorAll("[data-spr-role=block-root]"));
}

function getBlockRootsByBlockId(contentRoot: HTMLElement): Record<string, HTMLElement> {
    return keyBy(allBlockRoots(contentRoot), (blockRoot) => {
        const blockId = blockRoot.getAttribute("data-spr-block-id");
        if (!blockId) {
            throw new Error("data-spr-block-id not found in block root. This is likely to be a bug");
        }
        return blockId;
    });
}
export function getBlockRoot(blockId: string): HTMLElement | null {
    return document.querySelector(`[data-spr-block-id="${blockId}"]`);
}

/**
 * Inserts stub div elements for the react blocks (these are the portals where DynamicBlock.tsx
 * renders the blocks).
 *
 * For pre compiled blocks, the compiled_markup is injected into the page.
 * The block root element lives inside the compiled_markup.
 *
 */
export function addMissingBlockRoots(contentRoot: HTMLElement, blocks: readonly BlockDefinition[]) {
    // We only support moving block roots for root blocks. There's no reason to do this
    // for nested blocks.
    const allRootBlocks = rootBlocks(blocks);
    const cursor = new DOMCursor(contentRoot);

    for (let i = 0; i < allRootBlocks.length; i++) {
        const block = allRootBlocks[i];
        const prevBlock = allRootBlocks[i - 1];
        if (prevBlock) {
            cursor.positionAt(getLastNodeOf(prevBlock));
        } else {
            cursor.positionAt(null);
        }
        if (isPreCompiledBlock(block)) {
            if (preCompiledBlockExists(block)) {
                const start = getSentinelStart(block);
                const end = getSentinelEnd(block);
                for (const node of nodesBetween(start, end)) {
                    cursor.moveChildToCursor(node);
                }
                continue;
            } else {
                // For pre-compiled blocks we insert sentinel elements
                // that mark the start and end of the block.
                // PreCompiledBlock.tsx will render the block between these sentinels.
                log.debug("Adding sentinels for pre-compiled block", block.hrid);
                cursor.appendChild(makeSentinelStart(block));
                cursor.appendChild(makeSentinelEnd(block));
                continue;
            }
        }
        const existing = findBlockRoot(block.id);
        if (existing) {
            if (existing.id !== block.hrid) {
                log.debug(`Block hrid has changed from ${existing.id} to ${block.hrid}`);
                existing.id = block.hrid;
                existing.setAttribute(BLOCK_HRID_ATTRIBUTE, block.hrid);
            }

            // Move the existing block root to the correct position
            cursor.moveChildToCursor(existing);
            continue;
        } else {
            // Insert a new block root
            const blockRoot = initializeDynamicBlockRoot(block);
            log.debug("Adding block root for dynamic block", block.hrid);
            cursor.appendChild(blockRoot);
        }
    }
}

/**
 * If a block is removed from the page, or is moved inside a container block,
 * we must remove its block root/sentinels from the DOM.
 */
export function cleanupBlockRootsAndSentinels(contentRoot: HTMLElement, blocks: readonly BlockDefinition[]) {
    const newRootBlockIds = new Set(rootBlocks(blocks).map((it) => it.id));
    // Remove deleted dynamic block roots
    for (const blockRoot of allBlockRoots(contentRoot)) {
        const blockId = blockRoot.getAttribute("data-spr-block-id");
        if (!blockId) {
            throw new Error("data-spr-block-id not found in block root. This is likely to be a bug");
        }
        if (!newRootBlockIds.has(blockId)) {
            log.debug("Removing block root for", blockRoot.getAttribute(BLOCK_HRID_ATTRIBUTE));
            blockRoot.remove();
        }
    }
    // Remove deleted pre-compiled blocks
    for (const [blockId, start, end] of allBlockSentinels(contentRoot)) {
        if (!newRootBlockIds.has(blockId)) {
            log.debug("Removing block root for", start.getAttribute(BLOCK_HRID_ATTRIBUTE));
            for (const node of nodesBetween(start, end)) {
                contentRoot.removeChild(node);
            }
        }
    }
}

/**
 * Check if running {@link cleanupBlockRootsAndSentinels} is necessary.
 * The page renderer can avoid doing a synchronous React flush if cleanup is not
 * necessary. This matters because flushSync can make the UI unresponsive, so
 * we should only do it if it's necessary.
 * Make sure that it remains in sync with {@link cleanupBlockRootsAndSentinels}.
 */
export function needsBlockRootCleanup(contentRoot: HTMLElement, newBlocks: readonly BlockDefinition[]): boolean {
    const newRootBlockIds = new Set(rootBlocks(newBlocks).map((it) => it.id));
    for (const blockRoot of allBlockRoots(contentRoot)) {
        const blockId = blockRoot.getAttribute("data-spr-block-id");
        if (!blockId) {
            throw new Error("data-spr-block-id not found in block root. This is likely to be a bug");
        }
        if (!newRootBlockIds.has(blockId)) {
            return true;
        }
    }
    for (const [blockId] of allBlockSentinels(contentRoot)) {
        if (!newRootBlockIds.has(blockId)) {
            return true;
        }
    }
    return false;
}

export function rootBlocks(blocks: readonly BlockDefinition[]) {
    const blockParentId = getBlockParentIdMap(blocks);
    return blocks.filter((block) => !blockParentId[block.id]);
}

type BlockParentIdMap = Record<BlockDefinition["id"], BlockDefinition["id"] | undefined>;
export function getBlockParentIdMap(blocks: readonly BlockDefinition[]): Readonly<BlockParentIdMap> {
    const blockParentId: BlockParentIdMap = {};
    for (const block of blocks) {
        if (block.type === "container") {
            for (const { blockId } of block.container.slots) {
                if (blockId) {
                    blockParentId[blockId] = block.id;
                }
            }
        }
    }
    return blockParentId;
}

function makeSentinelStart(block: PreCompiledBlockDefinition) {
    const sentinel = document.createElement("div");
    sentinel.setAttribute("data-spr-role", "sentinel-start");
    sentinel.setAttribute("data-spr-sentinel-start-for", block.id);
    sentinel.setAttribute(BLOCK_HRID_ATTRIBUTE, block.hrid);
    return sentinel;
}
function makeSentinelEnd(block: PreCompiledBlockDefinition) {
    const sentinel = document.createElement("div");
    sentinel.setAttribute("data-spr-role", "sentinel-end");
    sentinel.setAttribute("data-spr-sentinel-end-for", block.id);
    sentinel.setAttribute(BLOCK_HRID_ATTRIBUTE, block.hrid);
    return sentinel;
}

export function getSentinelStart(block: Pick<PreCompiledBlockDefinition, "id">) {
    const sentinel = document.querySelector(`[data-spr-sentinel-start-for="${block.id}"]`);
    if (!sentinel) {
        throw new Error(`Sentinel start not found for pre-compiled block: ${block.id}`);
    }
    return sentinel;
}
function preCompiledBlockExists(block: PreCompiledBlockDefinition): boolean {
    return document.querySelector(`[data-spr-sentinel-start-for="${block.id}"]`) !== null;
}
function getSentinelEnd(block: PreCompiledBlockDefinition) {
    const sentinel = document.querySelector(`[data-spr-sentinel-end-for="${block.id}"]`);
    if (!sentinel) {
        throw new Error(`Sentinel start not found for pre-compiled block: ${block.id}`);
    }
    return sentinel;
}

function getLastNodeOf(block: BlockDefinition): Node {
    if (isPreCompiledBlock(block)) {
        return getSentinelEnd(block);
    }
    const blockRoot = findBlockRoot(block.id);
    if (!blockRoot) {
        throw new Error("Block root not found");
    }
    return blockRoot;
}

function allBlockSentinels(contentRoot: HTMLElement): [string, Element, Element][] {
    const result: [string, Element, Element][] = [];
    for (const start of contentRoot.querySelectorAll("[data-spr-sentinel-start-for]")) {
        const blockId = start.getAttribute("data-spr-sentinel-start-for");
        if (!blockId) {
            throw new Error("Sentinel start has no block id");
        }
        const end = contentRoot.querySelector(`[data-spr-sentinel-end-for="${blockId}"]`);
        if (!end) {
            throw new Error("Sentinel end not found for sentinel start");
        }
        result.push([blockId, start, end]);
    }
    return result;
}
export function getSentinelsFor(block: PreCompiledBlockDefinition): [Element, Element] {
    const start = getSentinelStart(block);
    const end = getSentinelEnd(block);
    return [start, end];
}

/**
 * Used for debug logging, when we have a sentinel or a block root element,
 * it's nice to have a human readable id to log.
 */
const BLOCK_HRID_ATTRIBUTE = "data-spr-block-hrid";
